/**
 * Modal lecture seule — fiche produit côté client. Affiche l'overlay
 * d'enrichissement saisi par le staff (photo, description, allergènes, badges)
 * par-dessus la donnée Cashpad (nom, prix, dispo). Fallback gracieux si
 * `enriched` est null/vide : on n'affiche que ce qui est renseigné.
 *
 * Le bouton "Ajouter au panier" est désactivé pour l'instant — placeholder
 * pour la phase commande/réservation à venir.
 */
import React, { useEffect } from 'react';
import './ProductDetailSheet.css';

export interface ProductEnriched {
  cashpadId: string;
  photoUrl: string | null;
  description: string | null;
  allergenes: string[];
  badges: string[];
  updatedAt: number;
  updatedBy: string | null;
}

export interface ViewableProduct {
  id: string;
  name: string;
  price: number;
  type: 'liquid' | 'solid' | 'other';
  available: boolean;
  enriched?: ProductEnriched | null;
}

interface Props {
  product: ViewableProduct | null;
  category: string | null; // pour libellé "Vins rouges · Boisson"
  onClose: () => void;
}

// Palette badges — un ton par badge connu, neutre en fallback.
const BADGE_TONES: Record<string, { bg: string; fg: string }> = {
  vegan:           { bg: '#e8f5e9', fg: '#2e7d32' },
  bio:             { bg: '#f1f8e9', fg: '#558b2f' },
  signature:       { bg: '#fff8e1', fg: '#b08600' },
  nouveau:         { bg: '#e3f2fd', fg: '#1565c0' },
  'fait maison':   { bg: '#fbe9e7', fg: '#bf360c' },
  'sans gluten':   { bg: '#fff9c4', fg: '#9a8500' },
  local:           { bg: '#efebe9', fg: '#5d4037' },
};
const BADGE_FALLBACK = { bg: '#eeeeee', fg: '#444' };

// Dégradé placeholder par type produit (quand pas de photo). Tons clairs pour
// que le fallback ne surcharge pas la fiche (cf hauteur réduite via aspect 2:1).
function placeholderGradient(type: 'liquid' | 'solid' | 'other'): string {
  if (type === 'liquid') return 'linear-gradient(135deg, #f3eaf0 0%, #e8d4dd 100%)';
  if (type === 'solid')  return 'linear-gradient(135deg, #f5e8d6 0%, #f7e0c2 100%)';
  return 'linear-gradient(135deg, #ececec 0%, #d8d8d8 100%)';
}
function placeholderEmoji(type: 'liquid' | 'solid' | 'other'): string {
  if (type === 'liquid') return '🍷';
  if (type === 'solid')  return '🍽️';
  return '📦';
}

export function ProductDetailSheet({ product, category, onClose }: Props) {
  // Masque la TabBar tant qu'une sheet modale est ouverte (cohérent avec /lieu
  // qui passe par FULLSCREEN_ROUTES). Ici le sheet est un overlay, donc on
  // toggle une class globale lue par TabBar.css.
  useEffect(() => {
    if (!product) return;
    document.body.classList.add('modal-sheet-open');
    return () => document.body.classList.remove('modal-sheet-open');
  }, [product]);

  if (!product) return null;
  const e = product.enriched ?? null;
  const hasPhoto = !!e?.photoUrl;
  const description = e?.description?.trim() ?? '';
  const allergenes = e?.allergenes ?? [];
  const badges = e?.badges ?? [];
  const typeLabel = product.type === 'liquid' ? 'Boisson' : product.type === 'solid' ? 'Cuisine' : '';
  const subtitle = [category, typeLabel].filter(Boolean).join(' · ');

  return (
    <div className="pds-backdrop" onClick={onClose}>
      <div className="pds-sheet" onClick={(ev) => ev.stopPropagation()}>
        <button className="pds-close" onClick={onClose} aria-label="Fermer">×</button>

        {/* Hero photo */}
        <div
          className={`pds-hero${hasPhoto ? '' : ' no-photo'}`}
          style={hasPhoto ? undefined : { background: placeholderGradient(product.type) }}
        >
          {hasPhoto ? (
            <img src={e!.photoUrl!} alt={product.name} />
          ) : (
            <span className="pds-hero-emoji" aria-hidden>{placeholderEmoji(product.type)}</span>
          )}
        </div>

        {/* Header : nom + prix */}
        <div className="pds-header">
          <div className="pds-header-text">
            <h2>{product.name}</h2>
            {subtitle && <p className="pds-subtitle">{subtitle}</p>}
          </div>
          <span className="pds-price">{product.price.toFixed(2).replace('.', ',')} €</span>
        </div>

        {/* Dispo */}
        <div className="pds-meta-row">
          <span className={`pds-availability ${product.available ? 'ok' : 'ko'}`}>
            {product.available ? 'Disponible' : 'Rupture'}
          </span>
        </div>

        {/* Badges */}
        {badges.length > 0 && (
          <div className="pds-section">
            <div className="pds-badges">
              {badges.map((b) => {
                const tone = BADGE_TONES[b] ?? BADGE_FALLBACK;
                return (
                  <span
                    key={b}
                    className="pds-badge"
                    style={{ background: tone.bg, color: tone.fg }}
                  >
                    {b}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Description */}
        {description && (
          <div className="pds-section">
            <p className="pds-description">{description}</p>
          </div>
        )}

        {/* Allergènes */}
        {allergenes.length > 0 && (
          <div className="pds-section pds-allergenes">
            <h3>
              <span aria-hidden>⚠️</span> Allergènes
            </h3>
            <p>{allergenes.join(', ')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
